import { camelizeKeys } from 'humps'
import { read, utils } from 'xlsx'
import { Schema } from 'yup'
import { readFileAsArrayBuffer } from 'utils/fileUploadHelper'

export type TCsvRow = Record<string, string>
export type TParsedCsv = TCsvRow[]

export type TCsvMatrix = string[][]

export type TCsvRowErrors = Record<number, string>
export type TCsvErrors = Record<number, TCsvRowErrors>

export interface ICsvErrorsResult {
  count: number
  errors: TCsvErrors | null
}

export const parseCsv = async (csvFile: File): Promise<TParsedCsv> => {
  const { file } = await readFileAsArrayBuffer(csvFile)
  const workbook = read(file)
  const sheetName = workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]
  const rows = utils.sheet_to_json<TCsvRow>(sheet, {
    raw: false,
    blankrows: false,
    defval: '',
  })

  return rows
}

export const csvToMatrix = async (csvFile: File): Promise<TCsvMatrix> => {
  const csvRows = await parseCsv(csvFile)
  const matrix = [Object.keys(csvRows[0])]
  csvRows.forEach(row => matrix.push(Object.values(row)))

  return matrix
}

export const validateCsv = async (
  csvFile: File,
  rowSchema: Schema<unknown>
): Promise<ICsvErrorsResult> => {
  const parsedCsv = await parseCsv(csvFile)
  const result: ICsvErrorsResult = {
    count: 0,
    errors: null,
  }

  const headers = Object.keys(camelizeKeys(parsedCsv[0]))

  for (let i = 0; i < parsedCsv.length; i += 1) {
    const csvRow = camelizeKeys(parsedCsv[i])

    if (i === 0) {
      const rowHeaders = Object.keys(csvRow)
      const emptyHeader = rowHeaders.find(header => header.toLowerCase().includes('empty'))

      if (emptyHeader) {
        const emptyHeaderIndex = rowHeaders.findIndex(header => header === emptyHeader)
        result.errors = result.errors || {}
        result.errors[i] = {}
        result.errors[i][emptyHeaderIndex] =
          'Please assign a column name or remove it from the CSV file.'
        result.count += 1
      }
    }

    try {
      rowSchema.validateSync(csvRow, { abortEarly: false, stripUnknown: true })
    } catch (errs) {
      const validationMessages = errs.inner.reduce(
        (acc: { [key: string]: string }, { message, path }: { path: string; message: string }) => {
          acc[path.split('.')[0]] = message
          return acc
        },
        {}
      )
      const rowIdx = i + 1
      result.errors = result.errors || {}
      result.errors[rowIdx] = {}
      Object.keys(validationMessages).forEach(field => {
        const columnIdx = headers.findIndex(header => header === field)
        if (result.errors) {
          result.errors[rowIdx][columnIdx] = validationMessages[field]
          result.count += 1
        }
      })
    }
  }

  return result
}
